import { GroupTreeNode } from '@aslanpay/portal-common-lib';
import useGroups from 'app/hooks/useGroups';
import usePots from 'app/hooks/usePots';
import { useMemo } from 'react';
import { formatGroupWithPots } from 'app/utils/groupsWithPot/groupsWithPot';

import { GroupTreeNodeComplete, GroupWithPot } from './types';

function buildAncestors(nodes: GroupTreeNode[], parents: string[] = []): GroupTreeNodeComplete[] {
  return nodes.map((node) => ({
    ...node,
    parents,
    children: node.children ? buildAncestors(node.children, [...parents, node.name]) : [],
  }));
}

function useGroupsWithPot(companyId: string | undefined, enabled = true) {
  const { data: simpleGroups = [], isLoading: isLoadingTree } = useGroups.useFetchGroupsTree(
    companyId,
    {},
    enabled
  );

  const { data: flatGroupsData, isLoading: isLoadingFlat } = useGroups.useFetchGroups(
    { companyId: companyId ?? '', page: 0, size: 9999 },
    !!companyId && enabled
  );

  const groups = useMemo(() => buildAncestors(simpleGroups), [simpleGroups]);

  const { data: pots, isLoading: isLoadingPots } = usePots.useFetchPots(
    {
      companyIds: companyId ? [companyId] : [],
      relatedType: ['GROUP'],
      page: 0,
      size: 999,
      status: ['ACTIVE'],
    },
    !!companyId && enabled
  );

  const isLoading = isLoadingTree || isLoadingFlat || isLoadingPots;

  const groupsWithPots: GroupWithPot[] = useMemo(
    () => (!isLoading ? formatGroupWithPots(groups, pots?.list ?? []) : []),
    [groups, pots, isLoading]
  );

  const flatGroupsWithPots = useMemo(() => {
    if (isLoading) return [];

    const potByRelatedId = new Map(
      (pots?.list ?? [])
        .filter((p): p is typeof p & { relatedId: string } => p.relatedId != null)
        .map((p) => [p.relatedId, p])
    );
    const flatList = flatGroupsData?.list ?? [];
    const nameById = new Map(flatList.map((g) => [g.id, g.name]));
    const parentById = new Map<string, string | null>(
      flatList.map((g) => [g.id, g.parentGroupId ?? null])
    );

    const ancestorCache = new Map<string, string[]>();

    function getAncestors(id: string): string[] {
      const cachedAncestors = ancestorCache.get(id);
      if (cachedAncestors) return cachedAncestors;
      const parentId = parentById.get(id);
      if (!parentId) {
        ancestorCache.set(id, []);
        return [];
      }
      const ancestors = [...getAncestors(parentId), nameById.get(parentId) ?? ''];
      ancestorCache.set(id, ancestors);
      return ancestors;
    }

    return flatList.map((group) => ({
      ...group,
      parents: getAncestors(group.id),
      pot: potByRelatedId.get(group.id),
    }));
  }, [flatGroupsData, pots, isLoading]);

  return {
    isLoading,
    groupsWithPots,
    flatGroups: flatGroupsData?.list ?? [],
    flatGroupsWithPots,
  };
}

export default useGroupsWithPot;
